import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, Clock, Activity, Trophy } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";

const HERO = "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBjaGFydCUyMGFic3RyYWN0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgyMjg4MDcxfDA&ixlib=rb-4.1.0&q=85";
const TRADER = "https://images.pexels.com/photos/18706593/pexels-photo-18706593.jpeg";

export default function Landing() {
  const { t } = useLang();
  const { user } = useAuth();
  const startHref = user ? "/lobby" : "/auth?mode=register";

  const features = [
    { icon: Activity, t: t("feat_1_t"), d: t("feat_1_d") },
    { icon: TrendingUp, t: t("feat_2_t"), d: t("feat_2_d") },
    { icon: Clock, t: t("feat_3_t"), d: t("feat_3_d") },
    { icon: Trophy, t: t("feat_4_t"), d: t("feat_4_d") },
  ];

  const rules = [
    "1 strumento reale: azione, commodity o cross di valute",
    "100.000 EUR/USD di budget iniziale",
    "candela da 5 secondi · 120 candele totali",
    "puntata in % del budget: 10% · 25% · 50%",
    "durata fissa: 10 minuti (600 secondi)",
    "prima puntata obbligatoria entro 40 secondi",
  ];

  return (
    <div className="min-h-screen bg-base text-white grain">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-base via-base/80 to-base/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-base via-transparent to-base/50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-28 md:pt-28 md:pb-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-signal border border-signal/40 px-3 py-1.5 mb-8">
              {t("hero_tag")}
            </span>
            <h1 className="font-heading font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              <span className="text-white">{t("hero_title_1")}</span><br />
              <span className="text-signal">{t("hero_title_2")}</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={startHref}
                data-testid="hero-cta"
                className="group inline-flex items-center gap-2 bg-signal text-black font-semibold px-7 py-3.5 rounded-sm hover:bg-white transition-colors"
              >
                {t("hero_cta")}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#how"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3.5 rounded-sm hover:bg-white/5 hover:border-white/40 transition-colors"
              >
                {t("hero_cta2")}
              </a>
            </div>
          </motion.div>
        </div>

        {/* slogan ticker */}
        <div className="relative border-y border-white/10 bg-base/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-wrap gap-x-10 gap-y-2 font-mono text-xs uppercase tracking-widest text-white/50">
            <span className="text-profit">// {t("slogan_1")}</span>
            <span>{t("slogan_2")}</span>
            <span className="text-loss">// {t("slogan_3")}</span>
            <span>{t("slogan_4")}</span>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-3">{t("feat_title")}</h2>
        <div className="w-16 h-1 bg-signal mb-14" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {features.map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-panel p-8 hover:bg-panelhover transition-colors"
            >
              <f.icon className="w-8 h-8 text-signal mb-6" strokeWidth={1.75} />
              <h3 className="font-heading font-bold text-lg mb-3">{f.t}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t border-white/10 bg-panel/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-3">{t("how_title")}</h2>
            <div className="w-16 h-1 bg-signal mb-10" />
            <ul className="space-y-4">
              {rules.map((r, i) => (
                <li key={r} className="flex items-start gap-4 font-mono text-sm">
                  <span className="text-signal tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-white/80">{r}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
            <img src={TRADER} alt="trader" className="w-full h-full object-cover grayscale contrast-125 opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-base to-transparent" />
            <div className="absolute bottom-6 left-6 font-mono text-xs uppercase tracking-widest text-signal">
              time to stop being polite and get real
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 flex flex-col md:flex-row justify-between gap-4 text-white/40 text-xs font-mono">
          <span>© 2026 SNETCH · the real trading match online</span>
          <div className="flex gap-5">
            <a href="/terms" data-testid="footer-terms" className="hover:text-signal transition-colors">Termini di Servizio</a>
            <a href="/privacy" data-testid="footer-privacy" className="hover:text-signal transition-colors">Privacy Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
